<!--
  Component to render <search-box-icons> element
  - Accepts the following properties:
  [id, type, name, placeholder, value, label, list, width]

  - Accepts optional callback property:
  [on-search-box-icon-selected]

  @example

    <search-box-icons
      ref="selectIcons"
      list='[{"id":"all","name":"All","color":"#54698d"},{"id":"announcement","name":"Announcement","color":"#62b7ed"}]'
      placeholder="Search ..."
    />

  -->

<template lang="html">
  <section class="s-icons-container">
    <div
      class="m-search-box"
      :id="id"
      v-clickoutside="onClickedOutside"
      @keydown.esc.stop="onEsc"
    >
      <div>
        <label v-if="label" class="m-text-input__label">{{ label }}</label>

        <div ref="displaySelectionContainer">
          <div class="m-selection" v-if="selectedItem">
            <div class="m-item-selected">
              <div
                class="m-item-selected__icon slds-icon_container"
                :class="getIconClass(selectedItem)"
              >
                <svg class="slds-icon">
                  <use :xlink:href="iconPath"></use>
                </svg>
              </div>
              <div class="m-content-selection">{{ iconText }}</div>
              <svg
                class="m-close-selection-icon"
                tabindex="0"
                @keydown.enter.esc="cancelSelectedItem"
                @click="cancelSelectedItem"
              >
                <use :xlink:href="getUtilityIcon('close')"></use>
              </svg>
            </div>
          </div>
        </div>

        <div ref="textInputContainer" v-if="!selectedItem">
          <text-input
            ref="textInputItem"
            :focus="shouldFocus"
            :type="type"
            :placeholder="placeHolderText"
            @on-text-input-focus="onInputFocus"
            @on-text-input-blur="onInputBlur"
            @on-text-input-change="setSearchKeyword"
            @click="onInputFocus"
            @on-up-arrow-pressed="onUpArrowPressed"
            @on-down-arrow-pressed="onDownArrowPressed"
            @on-tab-pressed="closeDropdown"
            @on-single-select-press-enter="onSingleSelectPressEnter"
          />
          <svg class="m-input-search-icon">
            <use :xlink:href="getUtilityIcon('search')"></use>
          </svg>
        </div>
      </div>
      <div class="m-result-list" v-if="showResultsList">
        <div ref="resultList">
          <ul>
            <li
              class="m-item"
              v-for="(item, index) in list"
              :key="`${item.name}-${index}`"
            >
              <search-box-icon-item
                :list-item="item"
                :exp="exp"
                @on-search-box-item-selected="onSearchBoxItemSelected"
              />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="js">
  import { ICONS } from "@/assets/icons/slds-icon-info";
  import iconsUri from "@/views/drag-drop-pages/components/utils/icons-uri";
  import TextInput from "@/components/TextInput";
  import SearchBoxIconItem from "@/views/drag-drop-pages/components/searchbox/searchicons/SearchBoxIconItem";
  import utils from "@/utils";
  import store from "@/store";

  export default  {
    name: "search-box-icons",
    components: {
      TextInput,
      SearchBoxIconItem
    },
    props: {
      /**
       * input properties
       * @values id, type, name, placeholder, value, label, list, width
       */
      id: {
        type: String
      },
      type: {
        type: String,
        default: "text"
      },
      name: {
        type: String
      },
      placeholder: {
        type: String
      },
      value: {
        type: Object
      },
      label: {
        type: String
      },
      list: {
        type: Array,
        default: function() {
          return ICONS;
        }
      },
      width: {
        type: Number
      },
      shouldFocus: {
        type: Boolean
      }
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      placeHolderText() {
        return this.getMessageText(this.messages.SEARCH);
      },
      iconText() {
        return this.selectedItem? this.selectedItem.name : "";
      },
      iconPath() {
        return utils.getIconPath(this.selectedItem);
      }
    },
    data () {
      return {
        selectedItem: null,
        exp: "",
        closeOptions: null,
        selectedId: -1,
        showResultsList: false
      }
    },
    mounted() {
      this.updateWidth(this.width);
    },
    watch: {
      width: function() {
        this.updateWidth(this.width);
      },
      value: {
        handler() {
          this.selectedItem = this.value;
        },
        immediate: true
      },
      closeOptions: function() {
        if(this.closeOptions) {
          this.closeDropdown();
        }
      }
    },
    methods: {
      removeSelectedClass(index, list) {
        if(index >= 0) {
          list[index].classList.remove("item-on-hover-highlight");
        }
      },
      selectAnItem(id) {
        if (this.$refs.resultList) {
          this.selectedId = id;
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
              (c) => c.children.length > 0);
          if (filteredList[id]) {
            filteredList[id].classList.add("item-on-hover-highlight");
            filteredList[id].scrollIntoView(false);
          }
        }
      },
      onUpArrowPressed() {
        if (this.selectedId > 0) {
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
              (c) => c.children.length > 0);
          this.removeSelectedClass(this.selectedId, filteredList);
          this.selectAnItem(this.selectedId - 1);
        }
      },
      onDownArrowPressed() {
        const filteredList =
          Array.prototype.filter.call(this.$refs.resultList.children[0].children,
            (c) => c.children.length > 0);
        if (this.selectedId < filteredList.length - 1) {
          this.removeSelectedClass(this.selectedId, filteredList);
          this.selectAnItem(this.selectedId + 1);
        }
      },
      onInputFocus() {
        this.isInputInFocus = true;
        this.showResultsList = true;
      },
      onInputBlur(val) {
        this.isInputInFocus = false;
        setTimeout(() => {
          const EVENT = "on-search-box-blur";
          if(this.$listeners[EVENT] || this._events[EVENT]) {
            this.$emit(EVENT, val);
          }
        }, 200);
      },
      onEsc() {
        this.closeDropdown();
      },
      onClickedOutside() {
        if (!this.isInputInFocus) {
          this.closeDropdown();
        }
      },
      closeDropdown() {
        this.isInputInFocus = false;
        this.showResultsList = false;
      },
      updateWidth(width) {
        if(width && Number(width)) {
          this.$refs.displaySelectionContainer.style.width = width + "px";
        }
      },
      getIconClass(item) {
        return utils.getIconClass(item);
      },

      getUtilityIcon(iconName) {
        return iconsUri.getUtilityUrl(iconName);
      },
      setSearchKeyword(val) {
        this.selectedId = -1;
        this.exp = val;
      },
      cancelSelectedItem() {
        this.selectedItem = null;
        this.emitEvent("on-search-box-icon-selected", this.selectedItem);
      },
      onSearchBoxItemSelected(item) {
        this.closeOptions = null; // reset to trigger close dropdown
        this.selectedItem = item;
        this.emitEvent("on-search-box-icon-selected", this.selectedItem);
        this.$nextTick(() => {
          this.closeOptions = true;
        });
      },
      emitEvent(eventName, data) {
        if(this.$listeners[eventName] || this._events[eventName]) {
          this.$emit(eventName, {id: this.id, icon: data});
        }
      },
      onSingleSelectPressEnter() {
        if (this.selectedId >= 0) {
          const filteredList =
            Array.prototype.filter.call(this.$refs.resultList.children[0].children,
              (c) => c.children.length > 0);
          if (filteredList[this.selectedId] && filteredList[this.selectedId].children[0]) {
            filteredList[this.selectedId].children[0].click();
          }
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  .s-icons-container {
    flex-basis: 100%;
    width: 100%;
  }

  .m-search-box {
    @include mixin-drag-drop-results-list;
    flex-direction: column;
    position: relative;
    display: flex;
  }

  .m-input-search-icon,
  .m-close-selection-icon {
    height: 1rem;
    width: 1rem;
    fill: #706e6b;
    position: absolute;
    right: 9px;
    bottom: 9px;
    cursor: pointer;
  }

  .m-close-selection-icon {
    bottom: 4px;
  }

  .text-input {
    padding-right: 2rem;
  }
  .m-selection {
    @include drag-drop-text-input;
    min-height: calc(1.875rem + 2px);
    padding: 0;
    margin: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .m-text-input__label {
    @include mixin-label;
  }
  .m-content-selection {
    overflow: hidden;
    word-break: break-all;
    max-width: calc(100% - 60px);
    font-size: 0.75rem;
    line-height: 2;
  }

  .m-item-selected {
    @include mixin-box;
    box-shadow: none;
    min-height: 24px;
    left: 2px;
    top: 2px;
    position: relative;
    display: flex;
    line-height: 1.7;
    font-size: 0.9rem;
    width: calc(100% - 5px);
    box-sizing: border-box;
    justify-content: flex-start;
  }
  .m-item-selected__icon {
    width: 1.25rem;
    height: 1.25rem;
    margin-left: 0.25rem;
    margin-top: 0.1rem;
    margin-right: 0.25rem;
    margin-bottom: 0.1rem;
  }
  .m-item-selected__icon {
    svg {
      @include mixin-drag-drop-list-item-svg-icon;
      background-color: transparent;
      width: 1.25rem;
      height: 1.25rem;
      margin-top: 0;
      margin-right: 0;
    }
  }
</style>
